import Button from "../../custom/button/button";
import success from "../../assets/success.png";
import styles from "./main.module.scss";
import { ReactComponent as Arrow } from "../../assets/arrow-back.svg";
import { ReactComponent as Info } from "../../assets/info.svg";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import { App, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import Select from "../../custom/select/select";
import classNames from "classnames";
import { useState } from "react";
import ProfileCard from "../profileCard/profileCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { applyForExam, baseUrl, getExams } from "../../requests";
import { useAtomValue } from "jotai";
import { userData } from "../../state";
import { AxiosError } from "axios";
import Input from "../../custom/input/input";
import { getDateWithoutTime } from "../../utils/formatDate";
import { errorMessage } from "../../utils/errorMessage";
import * as Yup from "yup";
import EmptyState from "../../custom/emptyState/emptyState";
import { isAfter, isEqual, parseISO } from "date-fns";
import { useRequery } from "../../hooks/useRequery";
import usePayment from "../../hooks/usePayment";

interface PaymentPayload {
  RouteId: number;
  Amount: number;
  Description: string;
  PaymentForm: string;
  DataId: number | string;
}

const initExamState = {
  examId: 0,
  examScheduleId: 0,
  examDate: "",
  examDateInWords: "",
  date: "",
  dateInWords: "",
  endDate: "",
  endDateInWords: "",
  time: "",
  amount: 0,
  score: 0,
};

const content = (
  <div className={styles.modalContent}>
    <img src={success} alt="success" />
    <h2>Congratulations!!!</h2>
    <p>
      Your exam fees have been paid successfully. This is a great step that will
      boost your overall score. Best of luck with your exams!
    </p>
  </div>
);

const Main = () => {
  const { makePayment, isPending } = usePayment();
  const navigate = useNavigate();
  const user = useAtomValue(userData);
  const { notification } = App.useApp();
  const [examDetails, setExamDetails] = useState(initExamState);
  useRequery({
    modalContent: content,
  });

  const applyForExamMutation = useMutation({
    mutationKey: ["apply-for-exam"],
    mutationFn: applyForExam,
  });
  const allExams = useQuery({
    queryKey: ["get-all-exams"],
    queryFn: () => getExams(user?.MemberId!),
  });
  const allExamData = allExams.data?.Data as Exam[];
  const currentDate = new Date();
  const futureAndPresentExams = allExamData?.filter((exam) => {
    const parsedDate = parseISO(exam?.ExamDate);
    return isAfter(parsedDate, currentDate) || isEqual(parsedDate, currentDate);
  });
  // const futureExams = allExamData?.filter((exam) => {
  //   const parsedDate = parseISO(exam?.Date);
  //   return differenceInDays(parsedDate, currentDate) > 14; // Ensures at least 15 days away
  // });
  // previous filter for exams

  const examOptions = futureAndPresentExams?.map((item) => (
    <option key={item.ExamId} value={`${item.ExamId}`}>
      {item.ExamName}
    </option>
  ));

  const applyForExamHandler = async (values: FormikValues) => {
    const payload: ApplyForExamPayload = {
      ExamId: examDetails?.examId,
      MembershipNo: user?.MemberId!,
      ExamScheduleId: examDetails?.examScheduleId,
      ExamDate: examDetails?.examDate,
      StartDate: examDetails?.date,
      EndDate: examDetails?.endDate,
      ExamTime: examDetails?.time,
    };
    try {
      await applyForExamMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          const paymentDetails = {
            RouteId: data?.Data?.RouteId || user?.MembershipTypeId!,
            Amount: examDetails?.amount,
            Description: data?.Data?.Description,
            PaymentForm: data?.Data?.PaymentForm,
            DataId: `${data?.Data?.DataId!}`,
          };
          handlePayment(paymentDetails);
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: errorMessage(error),
      });
      if (error?.response?.data?.Data) {
        const res = error?.response?.data?.Data;
        const paymentDetails = {
          RouteId: res?.RouteId,
          Amount: res?.Amount,
          Description: res?.Description,
          PaymentForm: res?.PaymentForm,
          DataId: res?.DataId,
        };
        handlePayment(paymentDetails);
      }
    }
  };

  const handlePayment = async (paymentDetails: PaymentPayload) => {
    const payload: ProcessPaymentPayload = {
      RouteId: paymentDetails?.RouteId,
      MembershipNo: user?.MemberId!,
      Amount: paymentDetails?.Amount,
      Description: paymentDetails?.Description,
      ReturnURL: `${baseUrl}/competency-assessment/apply`,
      PaymentForm: paymentDetails?.PaymentForm,
      DataId: paymentDetails?.DataId,
    };
    makePayment(payload, (data) => {
      const responseData = JSON.parse(data?.ResponsePayLoad) as PaymentDetails;
      window.location.href = responseData?.RedirectURL;
    });
  };

  const validationSchema = Yup.object().shape({
    exam: Yup.string().required("Required"),
    examDate: Yup.string().required("Required"),
    time: Yup.string().required("Required"),
    amount: Yup.number().required("Required"),
  });

  const formik = useFormik<FormikValues>({
    initialValues: {
      exam: examDetails?.examId ? `${examDetails?.examId}` : "",
      examDate: examDetails?.examDateInWords,
      time: examDetails?.time,
      amount: examDetails?.amount,
    },
    onSubmit: (values) => {
      applyForExamHandler(values);
    },
    enableReinitialize: true,
    validationSchema,
  });

  if (allExams.isLoading)
    return <Spin style={{ justifySelf: "center", alignSelf: "center" }} />;
  if (allExams.isError)
    return <div>{(allExams.error as AxiosError)?.message}</div>;

  return (
    <div>
      <section className={classNames("headingWithBackArrow")}>
        <button onClick={() => navigate("/competency-assessment")}>
          <Arrow />
        </button>
        <h3>Apply for Assessment</h3>
      </section>
      {futureAndPresentExams && futureAndPresentExams?.length > 0 ? (
        <section className={styles.tableContainer}>
          <ProfileCard addAboutLink showLicenseScore />
          {examDetails?.date ? (
            <section className={styles.smallGap}>
              <Info />
              <p>
                Registration for the selected exam is open from{" "}
                {examDetails?.dateInWords} to {examDetails?.endDateInWords}.
              </p>
            </section>
          ) : null}
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <section className={styles.fields}>
                <div>
                  <Field
                    name="exam"
                    as={Select}
                    label="Exam Name"
                    placeholder="Select"
                    options={examOptions}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      formik.setFieldValue("exam", e.target.value);
                      const selectedExam = allExamData?.find(
                        (item) => item.ExamId === parseInt(e.target.value)
                      ) as Exam;
                      if (selectedExam) {
                        const examDate = getDateWithoutTime(
                          selectedExam?.ExamDate
                        );
                        const examStartDate = getDateWithoutTime(
                          selectedExam?.Date
                        );
                        const examEndDate = getDateWithoutTime(
                          selectedExam?.EndDate
                        );

                        formik.setFieldValue("examDate", examDate);
                        setExamDetails({
                          examId: selectedExam.ExamId,
                          examScheduleId: selectedExam.ExamScheduleId,
                          examDate: selectedExam.ExamDate,
                          examDateInWords: examDate,
                          date: selectedExam.Date,
                          endDate: selectedExam.EndDate,
                          endDateInWords: examEndDate,
                          dateInWords: examStartDate,
                          time: selectedExam.Time,
                          amount: selectedExam.Amount,
                          score: selectedExam.Score,
                        });
                      } else setExamDetails(initExamState);
                    }}
                  />
                  {examDetails?.score ? (
                    <div>Score attainable: {examDetails?.score || "N/A"}</div>
                  ) : null}
                </div>
                <Field
                  name="examDate"
                  as={Input}
                  label="Exam Date"
                  placeholder="Exam date"
                  disabled
                />
              </section>
              <section className={styles.fields}>
                <Field
                  name="time"
                  as={Input}
                  label="Time"
                  placeholder="Exam time"
                  disabled
                />
                <Field
                  name="amount"
                  as={Input}
                  label="Amount"
                  placeholder="N200,000"
                  disabled
                />
              </section>
              <br />
              <br />
              <section className={classNames("spaceBetween")}>
                <Link to="https://www.cipmnigeria.org/">
                  Learn more about HRPL process
                </Link>
                <div className={styles.buttonGroup}>
                  <Button className={classNames("cancelBtn")} text="Cancel" />
                  <Button
                    type="submit"
                    isLoading={applyForExamMutation.isPending || isPending}
                    disabled={
                      applyForExamMutation.isPending ||
                      !formik.values.exam ||
                      isPending
                    }
                    text={
                      `Pay ` +
                      (examDetails?.amount ? `N${examDetails?.amount}` : "")
                    }
                  />
                </div>
              </section>
            </form>
          </FormikProvider>
        </section>
      ) : (
        <EmptyState text="No exams available yet" />
      )}
    </div>
  );
};

export default Main;
